import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useToast } from '@chakra-ui/react';

const useCheckAuth = (isPublicPage = false) => {
  const navigate = useNavigate();
  const location = useLocation(); // To determine the current page
  const toast = useToast();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get('/api/check-session'); // Check if the user is logged in
        const isAuthenticated = response.data.isAuthenticated;

        if (isAuthenticated && isPublicPage) {
          // If authenticated and on a public page, redirect to the dashboard
          navigate('/dashboard', { replace: true });
        } else if (!isAuthenticated && !isPublicPage) {
          // Try to refresh the token
          try {
            const refreshResponse = await axios.post('/api/refresh-token', {
              refreshToken: localStorage.getItem('refreshToken')
            });
            
            if (refreshResponse.data.success) {
              localStorage.setItem('token', refreshResponse.data.data.token);
              localStorage.setItem('refreshToken', refreshResponse.data.data.refreshToken);
              return;
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
          
          // If not authenticated and on a protected page, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        
        if (!isPublicPage) {
          toast({
            title: 'Authentication Error',
            description: 'Please log in again',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          navigate('/login', { replace: true });
        }
      }
    };

    checkSession();
  }, [navigate, isPublicPage, location.pathname, toast]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          try {
            const refreshResponse = await axios.post('/api/refresh-token', {
              refreshToken: localStorage.getItem('refreshToken')
            });
            
            if (refreshResponse.data.success) {
              localStorage.setItem('token', refreshResponse.data.data.token);
              localStorage.setItem('refreshToken', refreshResponse.data.data.refreshToken);
              
              // Retry the original request
              const config = error.config;
              config.headers['Authorization'] = `Bearer ${refreshResponse.data.data.token}`;
              return axios(config);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            toast({
              title: 'Session expired',
              description: 'Please log in again',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            navigate('/login');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, toast]);
};

export default useCheckAuth;
